import { useCallback } from "react";
import { axiosProtected } from "../../api/axios";

/**
 * Best-effort extractor for the new tokens from the refresh response.
 * The backend has shipped at least these shapes over time:
 *   { access_token, refresh_token?, expires_in, ... }
 *   { data: { access_token, refresh_token?, expires_in, ... }, message, status }
 *   { data: { tokens: { access_token, ... }, user }, ... }
 * We pull the first non-empty `access_token` / `expires_in` / `refresh_token`
 * we can find so a stray wrapper never causes a phantom "missing access_token"
 * → logout.
 */
function normalizeRefreshResponse(payload) {
  if (!payload || typeof payload !== "object") return null;

  const candidates = [
    payload,
    payload.data,
    payload.data?.data,
    payload.data?.tokens,
    payload.tokens,
    payload.result,
    payload.result?.tokens,
  ].filter((c) => c && typeof c === "object");

  let access_token;
  let refresh_token;
  let expires_in;
  let userPayload;

  for (const c of candidates) {
    if (!access_token && typeof c.access_token === "string" && c.access_token) {
      access_token = c.access_token;
    }
    if (!refresh_token && typeof c.refresh_token === "string" && c.refresh_token) {
      refresh_token = c.refresh_token;
    }
    if (expires_in == null && Number.isFinite(Number(c.expires_in))) {
      expires_in = Number(c.expires_in);
    }
    if (!userPayload && c.user && typeof c.user === "object") {
      userPayload = c.user;
    }
  }

  if (!access_token) return null;
  return { access_token, refresh_token, expires_in, user: userPayload, raw: payload };
}

export const useRefreshToken = () => {
  const refreshToken = useCallback(async (props) => {
    const access_token = props?.access_token || localStorage.getItem("access_token");
    const refresh_token = props?.refresh_token || localStorage.getItem("refresh_token");

    if (!access_token || access_token === "null" || access_token === "undefined") {
      throw new Error("Missing access token");
    }

    const hasRefreshToken =
      refresh_token && refresh_token !== "null" && refresh_token !== "undefined";

    // Some login responses only return access_token. Backend can accept either
    // { refresh_token } or { access_token } (with Bearer) for the same route.
    const body = hasRefreshToken ? { refresh_token } : { access_token };

    const { data } = await axiosProtected.post("refresh", body, {
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${access_token}`,
      },
    });

    const normalized = normalizeRefreshResponse(data);
    if (!normalized) {
      const err = new Error("Missing access token from refresh response");
      err.refreshResponse = data;
      throw err;
    }
    return normalized;
  }, []);

  return refreshToken;
};
